import { useCallback, useContext, useState } from "react";
import styles from "./Nudge.module.scss";
import { BaseButton } from "@/src/component/base/BaseButton";
import { DialogWithExit } from "@/src/component/base/DialogWithExit";
import { PortalComponent } from "@/src/component/partial/PortalComponent";
import { Nudge as NudgeType } from "@/src/type/strapi/component/object";
import { Box, Typography } from "@mui/material";
import { FormHooks, StyleHooks } from "@/src/hook";
import Image from "next/image";
import { TextBreakpointModule } from "../component";
import { Form } from "../DynamicForm/component/BuildDynamicForm/component";
import { AppContext } from "@/src/hook/provider/App/AppContext";

export function Nudge({ calloutCta, headline, subheader, disclaimer, formCta, formItems, destinations, trackingEvents }: NudgeType) {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const { crmLinkId, isLoading, submitForm } = FormHooks.useDynamicForm({
    destinations: (destinations?.data ?? []).map(({ attributes }) => attributes),
    trackingEvents,
  });
  const isLargeScreen = StyleHooks.useBreakPointUp("lg");
  const { pageSliderData } = useContext(AppContext);
  const isFirstSlide = pageSliderData?.activeIndex === 0;

  const onSubmit = (formData: Record<string, any>, currentStep: number) => {
    submitForm({ formData, currentStep });
  };

  const CalloutButton = useCallback(() => {
    return (
      <BaseButton
        className={styles.calloutCta}
        variant={calloutCta.variant}
        color={calloutCta.color}
        size={calloutCta.size || "small"}
        onClick={() => setIsModalVisible(true)}
      >
        <Image
          src="/image/icon_promo.svg"
          width={24}
          height={24}
          alt="Promo Icon"
          className={styles.calloutIcon}
        />
        {calloutCta.label}
      </BaseButton>
    );
  }, [calloutCta]);

  return (
    <>
      {isLargeScreen ? (
        <CalloutButton />
      ) : (
        isFirstSlide && (
          <PortalComponent>
            <CalloutButton />
          </PortalComponent>
        )
      )}

      {isModalVisible ? (
        <DialogWithExit
          dialogProps={{
            className: styles.dialog,
            open: isModalVisible,
            PaperProps: {
              className: styles.dialogPaper,
            },
            onClose: () => setIsModalVisible(false),
          }}
          dialogContentProps={{
            className: styles.dialogContent,
          }}
          onExit={() => setIsModalVisible(false)}
        >
          <>
            <Box className={styles.dialogContentHeader}>
              <TextBreakpointModule
                id="promo_headline"
                text={headline}
                className={styles.headline}
              />
              <TextBreakpointModule
                id="promo_subheader"
                text={subheader}
                className={styles.subheader}
              />
            </Box>

            <Form
              isDark={true}
              onSubmit={onSubmit}
              formId="NudgeForm"
              isLoading={isLoading}
              form={{
                submitButtonLabel: "Submit",
              }}
              step={{
                form_items: formItems,
              }}
              crmLinkId={crmLinkId}
              {...(formCta && {
                renderSubmitButton: () => {
                  return (
                    <BaseButton
                      type="submit"
                      variant={formCta.variant}
                      color={formCta.color || "primary"}
                      size={formCta.size || "small"}
                    >
                      {formCta.label}
                    </BaseButton>
                  );
                },
              })}
            />
            {!!disclaimer && (
              <Typography className={styles.disclaimer}>
                <span dangerouslySetInnerHTML={{ __html: disclaimer }} />
              </Typography>
            )}
          </>
        </DialogWithExit>
      ) : null}
    </>
  );
}
